class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.enhanceNotification();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);
  }

  enhanceNotification() {
    this.updateCartTotals();
    this.loadRelatedProducts();
    this.bindUpsellButtons();
  }

  updateCartTotals() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        const subtotal = this.notification.querySelector('[data-cart-notification-subtotal]');
        if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);

        const viewCartButton = document.getElementById('cart-notification-button');
        if (viewCartButton) viewCartButton.textContent = 'Bekijk winkelwagen (' + this.formatMoney(cart.total_price) + ')';

        const goalWrap = this.notification.querySelector('[data-free-shipping-goal]');
        const goalText = this.notification.querySelector('[data-free-shipping-text]');
        const goalFill = this.notification.querySelector('[data-free-shipping-fill]');
        if (goalWrap && goalText && goalFill) {
          const goal = parseInt(goalWrap.dataset.freeShippingGoal, 10) || 7500;
          const remaining = goal - cart.total_price;
          const progress = Math.min((cart.total_price / goal) * 100, 100);
          if (remaining <= 0) {
            goalText.innerHTML = 'Gratis verzending bereikt!';
          } else {
            goalText.innerHTML = 'Nog <strong>' + this.formatMoney(remaining) + '</strong> tot gratis verzending';
          }
          goalFill.style.width = progress + '%';
        }
      })
      .catch(() => {});
  }

  loadRelatedProducts() {
    const addedProduct = this.notification.querySelector('[data-added-product-id]');
    const upsell = this.notification.querySelector('[data-fr-cart-upsell]');
    const track = this.notification.querySelector('[data-fr-cart-upsell-track]');
    if (!addedProduct || !upsell || !track) return;

    const productId = addedProduct.dataset.addedProductId;
    if (!productId) return;

    track.innerHTML = '';
    upsell.hidden = true;

    fetch('/recommendations/products.json?product_id=' + encodeURIComponent(productId) + '&limit=4', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((data) => {
        const products = data && data.products ? data.products : [];
        if (!products.length) return;

        const cards = products.slice(0, 4).map((product) => {
          const variant = product.variants && product.variants[0] ? product.variants[0] : null;
          if (!variant) return '';
          const image = product.featured_image || (product.images && product.images[0]) || '';
          const price = product.price || variant.price || 0;
          const available = variant.available !== false;
          const disabled = available ? '' : ' disabled';
          const buttonText = available ? '+ Toevoegen' : 'Uitverkocht';

          return '<div class="fr-cart-upsell-card">' +
            '<a href="' + product.url + '" class="fr-cart-upsell-card__image">' +
              (image ? '<img src="' + image + '" alt="' + this.escapeHtml(product.title) + '" loading="lazy">' : '') +
            '</a>' +
            '<div class="fr-cart-upsell-card__body">' +
              '<div class="fr-cart-upsell-card__title">' + this.escapeHtml(product.title) + '</div>' +
              '<div class="fr-cart-upsell-card__stock">' + (available ? '✓ Op voorraad' : 'Niet op voorraad') + '</div>' +
              '<div class="fr-cart-upsell-card__price">' + this.formatMoney(price) + '</div>' +
              '<button type="button" class="fr-cart-upsell-card__button" data-upsell-variant-id="' + variant.id + '"' + disabled + '>' + buttonText + '</button>' +
            '</div>' +
          '</div>';
        }).join('');

        track.innerHTML = cards;
        upsell.hidden = false;
        this.bindUpsellButtons();
      })
      .catch(() => {});
  }

  bindUpsellButtons() {
    this.notification.querySelectorAll('[data-upsell-variant-id]').forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const variantId = button.dataset.upsellVariantId;
        if (!variantId) return;

        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'Toevoegen...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        })
          .then((response) => {
            if (!response.ok) throw new Error('Add failed');
            return response.json();
          })
          .then(() => {
            button.textContent = 'Toegevoegd';
            this.updateCartTotals();
            this.refreshCartIcon();
            setTimeout(() => {
              button.disabled = false;
              button.textContent = originalText;
            }, 1200);
          })
          .catch(() => {
            button.disabled = false;
            button.textContent = originalText;
          });
      });
    });
  }

  refreshCartIcon() {
    fetch('/cart?section_id=cart-icon-bubble', { credentials: 'same-origin' })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      })
      .catch(() => {});
  }

  escapeHtml(string) {
    return String(string || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

customElements.define('cart-notification', CartNotification);
